import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  trackClickTakeMeBack,
  trackGenerationStartSuccess,
  trackGenerationStartError,
} from '../../../../analytics';

import { FancyBtn } from '../../../../components/custom';
import { Button } from '../../../../components/general';

import { URL } from '../../../../constants';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';
import useVbData from '../../../../hooks/useVbData';

import { generateCreations } from '../../../../services/api/creations';

import { useProjectStore } from '../../../../stores/project';
import { useVideoBuilderStore } from '../../../../stores/videoBuilder';

import { ClipSelection, WildcardFiller } from '../../../../types';
import { isEmpty, getUrl } from '../../../../utils';

interface Props {
  toGenerateText: string;
  selectedPeopleIds: string[];
}

const GenerationBlock: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { updateCurrentEvent } = useProjectStore();
  const { clipSelections } = useVideoBuilderStore();

  const { toGenerateText, selectedPeopleIds } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);

  // sample project data
  const { isSample, sampleProjectData, updateSampleProjectData } =
    useSampleProjectData(eventId);

  const { updateToGeneratePeopleIds } = useVbData();

  const navigate = useNavigate();

  const completeClipSelections = useMemo(() => {
    const completeClipSelections: ClipSelection[] = [];

    clipSelections.forEach((group: ClipSelection) => {
      // if group has all shots add it to return array
      if (
        group.wildcardsFiller.every((clip: WildcardFiller) => clip.clipId) &&
        selectedPeopleIds.includes(group.faceId)
      )
        completeClipSelections.push(group);
    });

    return completeClipSelections;
  }, [clipSelections, selectedPeopleIds]);

  const onSuccessfulGenerationStart = () => {
    trackGenerationStartSuccess(isSample);

    // remove eventId from generatedProjects (for GA)
    const generatedProjects = JSON.parse(
      localStorage.getItem('generatedProjects') ?? '[]',
    );
    const projectsUpd = generatedProjects.filter(
      (id: string) => id !== eventId,
    );
    localStorage.setItem('generatedProjects', JSON.stringify(projectsUpd));

    navigate(getUrl(URL.VIDEO_BUILDER, eventId));
  };

  // usual project
  const handleGenerationStart = async () => {
    setIsSubmitting(true);

    try {
      await generateCreations(eventId, completeClipSelections);

      updateToGeneratePeopleIds(
        completeClipSelections.map((group) => group.faceId),
      );

      onSuccessfulGenerationStart();

      await updateCurrentEvent();
    } catch (err: any) {
      trackGenerationStartError(err.message);
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // sample project – imitates start of generation
  const handleSampleGenerationStart = () => {
    setIsSubmitting(true);

    const completeIds: string[] = [];

    clipSelections.forEach((group: ClipSelection) => {
      const person = sampleProjectData.people.find(
        (p: any) => p.face.elementId === group.faceId,
      );

      // if creation is already 'generated' – skip it
      if (person.isGenerated) return;

      const creationData = sampleProjectData.creations[group.faceId];

      // match creation based on selected clips
      creationData.forEach((creation: any) => {
        let isMatching = true;

        group.wildcardsFiller.forEach((wildcard) => {
          if (
            creation.matchingClips[wildcard.wildCardShotIndex] !==
            wildcard.clipId
          ) {
            isMatching = false;
            return;
          }
        });

        creation.isSelected = isMatching;
      });

      // set person as complete/incomplete depending on whether they have a matching creation
      if (creationData.some((creation: any) => creation.isSelected)) {
        person.isComplete = true;
        completeIds.push(group.faceId);
      }
    });

    sampleProjectData.event.stage = 'generation';
    sampleProjectData.isVideosGenerated = false;
    updateSampleProjectData(sampleProjectData);

    updateToGeneratePeopleIds(completeIds);

    setTimeout(() => {
      onSuccessfulGenerationStart();
      setIsSubmitting(false);
    }, 500);
  };

  const handleTakeMeBack = () => {
    navigate(getUrl(URL.VIDEO_BUILDER, eventId));
    trackClickTakeMeBack('generation', isSample);
  };

  return (
    <div className="w-full flex flex-col items-center gap-4">
      <div className="flex flex-col items-center text-center gap-2">
        <div className="text-lg font-bold">
          Ready to generate personalised highlight reels?
        </div>

        <div className="italic">
          We'll generate reels for {toGenerateText} checked-in people.
        </div>
      </div>

      <FancyBtn
        text="Generate"
        onClick={isSample ? handleSampleGenerationStart : handleGenerationStart}
        isSubmitting={isSubmitting}
        isDisabled={isEmpty(completeClipSelections)}
      />

      <Button onClick={handleTakeMeBack} variant="link" colour="black">
        Not yet, take me back
      </Button>
    </div>
  );
};

export default GenerationBlock;
